import * as PIXI from 'pixi.js';
import { Chaining, DefaultMembraneColor } from "../../../shared/constants";
import { ElementDisplayInfo } from '../../../shared/elementDisplayInfo';
import { cellLocations } from '../../../shared/generated/assetLocations';
import { AssetLocation } from '../../../shared/utils';
import { Colors } from '../palettes/colorPalette';
import antibodyNormalMap from "./shaders/fragment/antibodyNormalMap";
import bloodcell from './shaders/fragment/bloodcell';
import ColorerFrag from "./shaders/fragment/colorer";
import lightingFrag from "./shaders/fragment/lighting";
import NoiseFrag from "./shaders/fragment/noise";
import noiseMixer from './shaders/fragment/noiseMixer';
import basicNormals from "./shaders/vertex/basicNormals";
import DefaultVert from "./shaders/vertex/default";
import hexTileLighting from './shaders/vertex/hexTileLighting';
import lightingVert from "./shaders/vertex/lighting";
import vertexColorFrag from './shaders/fragment/vertexColor';
import vertexColorVert from './shaders/vertex/vertexColor';
import { Extract } from 'pixi.js';
import { loadSpriteAssets } from './assetLoader';

const NoiseSize = 256;

//TODO: better place for these?
export let coloredSpritesheet: HTMLImageElement;

export class SpriteFactory {
	ready: Promise<void>;

	cellSprites!: PIXI.BaseTexture;
	colorSprites!: PIXI.BaseTexture;
	depthMaps!: PIXI.BaseTexture;
	ciliaSprites!: PIXI.BaseTexture;
	glowSprites!: PIXI.BaseTexture;
	membraneDepthTexture!: PIXI.BaseTexture;

	voronoiNoise!: PIXI.Texture;
	lensDirtTexture!: PIXI.Texture;

	depthTexture!: PIXI.Texture;
	membraneMap!: PIXI.RenderTexture;
	membraneMapMesh: PIXI.Mesh | null = null;
	membraneNoiseTime = 0;

	private colorRenderTexture!: PIXI.RenderTexture;

	//Shaders
	private spriteShader = new PIXI.Program(lightingVert, lightingFrag);
	private spriteBloodCellShader = new PIXI.Program(hexTileLighting, bloodcell);

	private depthNoiseMixer = new PIXI.Program(lightingVert, noiseMixer);

	private normalMapMaker = new PIXI.Program(basicNormals, antibodyNormalMap)

	//private backgroundNoise = new PIXI.Program(DefaultVert, backgroundNoise);
	private chainShader = new PIXI.Program(vertexColorVert, vertexColorFrag);

	private renderer: PIXI.Renderer;
	private loader: PIXI.Loader;
	private textureAssets: { [key: string]: PIXI.Texture } = {};

	constructor(pixiApp: PIXI.Application) {
		this.renderer = pixiApp.renderer;

		//Load font for use through pixi asset key system
		this.loader = new PIXI.Loader();
		this.loader.add('arial', 'assets/arial.fnt');
		this.loader.load();
		const fontLoadready = new Promise<void>((resolve) => {
			this.loader.onComplete.add(async (loader: PIXI.Loader) => {
				resolve();
			});
		});

		this.ready = new Promise<void>(async (resolve, _) => {
			const { spriteBaseTextureAssets, spriteTextureAssets } = await loadSpriteAssets();

			this.cellSprites = spriteBaseTextureAssets['cellSpriteSheet'];
			this.depthMaps = spriteBaseTextureAssets['depthSpriteSheet'];
			this.glowSprites = spriteBaseTextureAssets['glowSpriteSheet'];
			this.ciliaSprites = spriteBaseTextureAssets['ciliaSpriteSheet'];
			this.membraneDepthTexture = spriteBaseTextureAssets['membraneDepthSpriteSheet'];

			this.voronoiNoise = spriteTextureAssets['voronoi'];
			this.voronoiNoise.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
			this.lensDirtTexture = spriteTextureAssets['lensDirt'];
			//this.lensDirtTexture.baseTexture.wrapMode = PIXI.WRAP_MODES.REPEAT;
			this.textureAssets = spriteTextureAssets;

			this.depthTexture = new PIXI.Texture(this.depthMaps);

			this.prepareGPUSpriteSheets();

			//Get a CPU copy of the gpu colored sprites
			const imageElement = this.renderer.plugins.extract.image(this.colorRenderTexture);
			this.colorSprites = PIXI.BaseTexture.from(imageElement);

			//TODO: Collapse this to one thing when the UI side of this is figured out
			coloredSpritesheet = imageElement;

			//Set locations for shop assets
			for (const display of ElementDisplayInfo.values()) {
				display.location = (cellLocations as any)[display.asset];
			}

			this.updateDepthNoise(this.membraneNoiseTime);

			await fontLoadready;

			resolve();
		})
	}

	getTexture(key: string) {
		return this.textureAssets[key];
	}

	getDepthMap(location: AssetLocation) {
		return this.getSpriteFromSpritesheet(this.depthMaps, location);
	}

	getMembraneDepthMap(location: AssetLocation) {
		return this.getSpriteFromSpritesheet(this.membraneMap.baseTexture, location);
	}

	getColorMap(location: AssetLocation) {
		return this.getSpriteFromSpritesheet(this.colorSprites, location);
	}

	getGlowMap(location: AssetLocation) {
		return this.getSpriteFromSpritesheet(this.glowSprites, location);
	}

	private getSpriteFromSpritesheet(atlas: PIXI.BaseTexture, location: AssetLocation) {
		const rect = new PIXI.Rectangle(location.x, location.y, location.width, location.height);
		const texture = new PIXI.Texture(atlas, rect)
		return texture;
	}


	getCiliaSprites() {
		this.ciliaSprites.scaleMode = PIXI.SCALE_MODES.NEAREST;
		return this.ciliaSprites;
	}

	// setSprite(entity: Entity) {
	// 	const renderable = entity.getMutableComponent(Renderable);
	// 	const transformComp = entity.getComponent(Transform);
	// 	const antigen = entity.getComponent(Antigen);
	// 	const color = !antigen ? AntigenColor.NoColor : antigen.color;
	// 	const transform = transformComp.getRenderTransform();
	// 	const { position, scale } = transform;

	// 	if (renderable.sprite) {
	// 		renderable.sprite.destroy();
	// 	}
	// 	if (renderable.internalSprite) {
	// 		renderable.internalSprite.destroy();
	// 	}

	// 	const isBloodCell = entity.hasComponent(BloodCell);
	// 	const isImmuneCell = entity.hasComponent(Antibody);
	// 	const tiling = isBloodCell && entity.getComponent(HexTileable) || undefined as HexTileable | undefined;

	// 	renderable.sprite = this.makeSprite(position, scale, color, renderable.assetLocation, isImmuneCell, tiling);
	// 	if (isImmuneCell) {
	// 		renderable.internalSprite = this.makeSprite(position, scale, color, renderable.assetLocationInternal, false, tiling);
	// 	}
	// }

	// makeSprite(position: vec2, scale: number, color: AntigenColor, location: AssetLocation, isShell: boolean, bloodCellTiling?: HexTileable) {
	// 	const antiColor = getAntigenColor(color);
	// 	const antigenRGB = PIXI.utils.hex2rgb(antiColor);
	// 	const texture = this.colorSprites;

	// 	const width = location.width;
	// 	const height = location.height;

	// 	let shader = this.spriteShader;
	// 	const uniforms: Record<string, Object> = {
	// 		texture,
	// 		normalMap: this.normalMaps,
	// 		inflatedNormalMap: this.inflatedNormalMaps,
	// 		heightmap: this.depthMaps,
	// 		antigenColor: antigenRGB,
	// 		lightPos: [-0.1, -0.5, -1], //temp value gets replaced fast
	// 		anchor: [0.5, 0.5],
	// 		tint: [0, 0, 0, 0],
	// 		antigenIntensity: 0.0,
	// 		color: PIXI.utils.hex2rgb(0x555555), //only for vertex lighting, this is a test TODO: remove
	// 		backgroundColor: PIXI.utils.hex2rgb(Colors.BackgroundRaised),
	// 		aspect: texture.width / texture.height,
	// 		radius: (location.width / texture.width) / 2,
	// 		inflation: 0,
	// 		isTransparentShell: isShell
	// 	};

	// 	//TODO: Generalize for all tiles
	// 	if (bloodCellTiling) {
	// 		shader = this.spriteBloodCellShader;
	// 		uniforms.hexTextureUVOffset = [
	// 			(cellLocations["bloodcell-hex"].x / texture.width) - (location.x / texture.width),
	// 			(cellLocations["bloodcell-hex"].y / texture.height) - (location.y / texture.height),
	// 		]
	// 		//Because the spritesheet dimensions are unknown, we have to inform the shader where the center uv should be.
	// 		uniforms.center = [
	// 			(location.x + location.width / 2) / texture.width,
	// 			(location.y + location.height / 2) / texture.height
	// 		];
	// 	}

	// 	const sprite = createQuadMesh(width, height, shader, uniforms, 0.5, { atlasW: this.cellSprites.width, atlasH: this.cellSprites.height, location });

	// 	if (bloodCellTiling) {
	// 		const tiling = bloodCellTiling
	// 		const idx = tiling.computeTilingIndex();
	// 		tiling.buffer = new Float32Array([idx, idx, idx, idx]);
	// 		const pixiBuffer = new PIXI.Buffer(tiling.buffer)
	// 		sprite.geometry.addAttribute("hexagonTilingIndex", pixiBuffer, 1);
	// 	}

	// 	sprite.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
	// 	sprite.x = position.x;
	// 	sprite.y = position.y;
	// 	sprite.scale.set(scale);

	// 	return sprite;
	// }



	updateDepthNoise(delta: number) {
		this.membraneNoiseTime += delta;
		if (this.membraneNoiseTime > 1) this.membraneNoiseTime -= 1;
		const noiseScale = 2.5;
		const loopsPerSecond = 80;
		const timeScaler = (1 / loopsPerSecond) / this.voronoiNoise.width;

		if (this.membraneMap === null || this.membraneMapMesh === null) {
			const uniforms = {
				depth: this.membraneDepthTexture,
				noise: this.voronoiNoise,
				time: this.membraneNoiseTime,
				noiseUvScaler: [
					noiseScale * this.membraneDepthTexture.width / this.voronoiNoise.width,
					noiseScale * this.membraneDepthTexture.height / this.voronoiNoise.height
				],
			};
			this.membraneMapMesh = createQuadMesh(this.membraneDepthTexture.width, this.membraneDepthTexture.height, this.depthNoiseMixer, uniforms);
			this.membraneMapMesh.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
			this.membraneMap = this.renderer.generateTexture(this.membraneMapMesh, PIXI.SCALE_MODES.LINEAR, 1);
		}
		else {
			this.membraneMapMesh.shader.uniforms.time = this.membraneNoiseTime * timeScaler;
			this.renderer.render(this.membraneMapMesh, this.membraneMap);
			this.membraneMap.requiresUpdate = true;
		}
		return this.membraneMap;
	}

	private prepareGPUSpriteSheets() {
		//Apply color palette
		const uniforms = {
			sprites: this.cellSprites,
			heightmap: this.depthMaps,
		};

		//Recreate this shader every time because the palette may have changed
		const colorTextureMaker = new PIXI.Program(basicNormals, ColorerFrag);
		const shader = colorTextureMaker;
		const mesh = createQuadMesh(this.cellSprites.width, this.cellSprites.height, shader, uniforms);
		mesh.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;
		this.colorRenderTexture = this.renderer.generateTexture(mesh, PIXI.SCALE_MODES.LINEAR, 1);

		//Make normal map sprite sheet from the depth maps
		// const normalMapUniforms = {
		// 	depth: this.depthMaps,
		// 	sampleStepX: 1 / this.depthMaps.width,
		// 	sampleStepY: 1 / this.depthMaps.height,
		// };
		// const normalMesh = createQuadMesh(this.depthMaps.width, this.depthMaps.height, this.normalMapMaker, normalMapUniforms);
		// normalMesh.blendMode = PIXI.BLEND_MODES.NORMAL_NPM;

		// this.normalMaps = this.renderer.generateTexture(normalMesh, PIXI.SCALE_MODES.LINEAR, 1);
	}
}

export function createQuadMesh(
	width: number, height: number,
	program: PIXI.Program,
	uniforms: any,
	anchor = 0,
	atlasInfo: { atlasW: number, atlasH: number, location: AssetLocation } | null = null
) {
	const zeroPoint = -anchor;
	const onePoint = 1 - anchor;

	let uvs: Array<number>;
	if (!atlasInfo) {
		uvs = [
			0, 0,
			1, 0,
			1, 1,
			0, 1
		];
	}
	else {
		const location = atlasInfo.location;
		const tu = location.x / atlasInfo.atlasW;
		const tv = location.y / atlasInfo.atlasH;
		const bu = (location.x + location.width) / atlasInfo.atlasW;
		const bv = (location.y + location.height) / atlasInfo.atlasH;
		uvs = [
			tu, tv,
			bu, tv,
			bu, bv,
			tu, bv
		];
	}

	const quad = new PIXI.Geometry()
		.addAttribute('aVertexPosition', [
			width * zeroPoint, height * zeroPoint,
			width * onePoint, height * zeroPoint,
			width * onePoint, height * onePoint,
			width * zeroPoint, height * onePoint,
		], 2)
		.addAttribute('uv', uvs, 2)
		.addAttribute('normal', [
			-1, 1, -1,
			1, 1, -1,
			1, -1, -1,
			-1, -1, -1,
		], 3)
		.addIndex([
			0, 1, 2,
			0, 2, 3
		]);

	const shader = new PIXI.Shader(program, uniforms);
	return new PIXI.Mesh(quad, shader);
}

async function generatePerlinNoise(renderer: PIXI.Renderer) {
	const quad = new PIXI.Geometry()
		.addAttribute('aVertexPosition', [
			0, 0,
			NoiseSize, 0,
			NoiseSize, NoiseSize,
			0, NoiseSize
		], 2)
		.addAttribute('uv', [
			0, 0,
			1, 0,
			1, 1,
			0, 1
		], 2)
		.addIndex([
			0, 1, 2, 0, 2, 3
		]);
	const shader = PIXI.Shader.from(DefaultVert, NoiseFrag, {

	});
	const mesh = new PIXI.Mesh(quad, shader);
	return renderer.generateTexture(mesh, PIXI.SCALE_MODES.LINEAR, 1);
}

